import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import SecurityIcon from "@mui/icons-material/Security";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import PaymentIcon from "@mui/icons-material/Payment";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ReportIcon from "@mui/icons-material/Report";
import BackupIcon from "@mui/icons-material/Backup";
import LogoutIcon from "@mui/icons-material/Logout";
import CategoryIcon from "@mui/icons-material/Category";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useNavigate } from "react-router-dom";
import { Configuration } from "../../../assets/data/config";
import Swal from "sweetalert2";

// Import admin profile image placeholder
import peacemakerImg from "../../../assets/images/homeimages/peacemakerImg.png";

const drawerWidth = 300;

const AdminDashboardSidebar = ({
  sidebarOpen,
  mobileOpen,
  handleMobileToggle,
  onMenuSelect,
  selectedMenu,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Get admin data from localStorage
  const adminDataStr = localStorage.getItem("adminData");
  const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;

  const adminName = adminData?.full_name || adminData?.username || "Admin User";
  const adminRole = adminData?.role || "ADMIN";
  const usertype = adminData?.usertype;

  const handleLogout = async () => {
    try {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: "Logout",
        text: "Are you sure you want to logout?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, logout!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }

      // Show loading
      Swal.fire({
        title: "Logging out...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Call logout API
      const response = await fetch(
        `${Configuration.apiService.key}admin/logout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        // Clear admin data
        localStorage.removeItem("adminData");
        localStorage.removeItem("isAdminLoggedIn");

        // Close loading and redirect
        Swal.close();
        navigate("/adminlogin");
      } else {
        throw new Error("Logout API failed");
      }
    } catch (error) {
      console.error("Logout error:", error);

      // Close loading dialog if open
      Swal.close();

      // Even if API fails, clear local data and redirect
      localStorage.removeItem("adminData");
      localStorage.removeItem("isAdminLoggedIn");

      Swal.fire({
        title: "Logged out",
        text: "You have been logged out successfully.",
        icon: "info",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => {
        navigate("/adminlogin");
      });
    }
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Profile Section */}
      <Box
        sx={{
          minHeight: 165,
          position: "relative",
          backgroundColor: "#fff",
          borderRadius: 2,
          m: 2,
          boxShadow: 1,
          overflow: "hidden",
          pb: 2,
          p: 1,
          display: "none",
        }}
      >
        {/* Background image */}
        <Box
          sx={{
            height: 80,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
          }}
          className="dashboardSidebarBg"
        />

        {/* Avatar */}
        <Box>
          <Avatar
            alt={adminName}
            src={peacemakerImg}
            sx={{
              width: 50,
              height: 50,
              position: "absolute",
              top: 60,
              left: "14%",
              transform: "translateX(-50%)",
              border: "2px solid #fff",
              bgcolor: "#214F7D",
            }}
          >
            <Typography variant="h6" color="white">
              {adminName.charAt(0).toUpperCase()}
            </Typography>
          </Avatar>
        </Box>

        {/* Name and Role */}
        <Box sx={{ mt: 1, textAlign: "left", px: 2, pl: 8 }}>
          <Typography
            variant="subtitle1"
            fontWeight={100}
            className="profileName"
          >
            {adminName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              backgroundColor: "#00000024",
              padding: "4px 6px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: 11,
            }}
          >
            {adminRole}
          </Typography>
        </Box>
      </Box>

      {/* Menu List - Admin Modules */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {/* Dashboard Overview */}
        {/* <SidebarItem
          text="Dashboard Overview"
          icon={<DashboardIcon />}
          active={selectedMenu === 'Dashboard Overview'}
          onClick={() => onMenuSelect('Dashboard Overview')}
        /> */}

        {/* User Management - Only show if not Entity */}
        {usertype !== 'Entity' && (
          <SidebarItem
            text="Client Management"
            icon={<ManageAccountsIcon />}
            active={selectedMenu === "User Management"}
            onClick={() => onMenuSelect("User Management")}
          />
        )}

        {/* Category Management - Only show if not Entity */}
        {usertype !== 'Entity' && (
          <SidebarItem
            text="Category Management"
            icon={<CategoryIcon />}
            active={selectedMenu === "Category Management"}
            onClick={() => onMenuSelect("Category Management")}
          />
        )}
        
        {/* Entity Management */}
        <SidebarItem
          text="Entity Management"
          icon={<BusinessIcon />}
          active={selectedMenu === "Entity Management"}
          onClick={() => onMenuSelect("Entity Management")}
        />

        {/* Affiliate Details - Only show if not Entity */}
        {usertype !== 'Entity' && (
          <SidebarItem
            text="Gifts"
            icon={<AccountBalanceWalletIcon />}
            active={selectedMenu === "Gifts"}
            onClick={() => onMenuSelect("Gifts")}
          />
        )}

        {/* User Access - Only show if not Entity */}
        {usertype !== 'Entity' && (
          <SidebarItem
            text="User Access"
            icon={<AdminPanelSettingsIcon />}
            active={selectedMenu === "User Access"}
            onClick={() => onMenuSelect("User Access")}
          />
        )}

        {/* Entity Action Menus - Only show if usertype is Entity */}
        {usertype === 'Entity' && (
          <>
            <SidebarItem
              text="View Referrals"
              icon={<GroupsIcon />}
              active={selectedMenu === "View Referrals"}
              onClick={() => onMenuSelect("View Referrals")}
            />
            <SidebarItem
              text="Copy Referral Link"
              icon={<ContentPasteIcon />}
              active={selectedMenu === "Copy Referral Link"}
              onClick={() => onMenuSelect("Copy Referral Link")}
            />
            <SidebarItem
              text="View Visiting Card"
              icon={<CreditCardIcon />}
              active={selectedMenu === "View Visiting Card"}
              onClick={() => onMenuSelect("View Visiting Card")}
            />
            <SidebarItem
              text="View Certificate"
              icon={<WorkspacePremiumIcon />}
              active={selectedMenu === "View Certificate"}
              onClick={() => onMenuSelect("View Certificate")}
            />
            <SidebarItem
              text="View Entity Details"
              icon={<VisibilityIcon />}
              active={selectedMenu === "View Entity Details"}
              onClick={() => onMenuSelect("View Entity Details")}
            />
          </>
        )}

        {/* Payment Management */}
        {/* <SidebarItem
          text="Payment Management"
          icon={<PaymentIcon />}
          active={selectedMenu === 'Payment Management'}
          onClick={() => onMenuSelect('Payment Management')}
        /> */}

        {/* Analytics & Reports */}
        {/* <SidebarItem
          text="Analytics & Reports"
          icon={<AnalyticsIcon />}
          active={selectedMenu === 'Analytics & Reports'}
          onClick={() => onMenuSelect('Analytics & Reports')}
        /> */}

        {/* System Settings */}
        {/* <SidebarItem
          text="System Settings"
          icon={<SettingsIcon />}
          active={selectedMenu === 'System Settings'}
          onClick={() => onMenuSelect('System Settings')}
        /> */}
      </List>

      {/* Logout Section */}
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <SidebarItem
          text="Logout"
          icon={<LogoutIcon />}
          active={false}
          onClick={handleLogout}
          sx={{
            color: "#ff6b6b",
            "&:hover": {
              backgroundColor: "rgba(255, 107, 107, 0.1)",
              color: "#ff5252",
            },
          }}
        />
      </Box>

      {/* Footer */}
      <Box
        sx={{
          textAlign: "center",
          p: 2,
          borderTop: "1px solid #eee",
          fontSize: 12,
          color: "#fff",
        }}
      >
        ADMIN PANEL V1.0.0
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: sidebarOpen ? drawerWidth : 0 },
        flexShrink: { sm: 0 },
      }}
      aria-label="sidebar"
      className="sideBarSection"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileToggle}
        ModalProps={{ keepMounted: true }}
        className="mobileSidebarDrawer"
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: sidebarOpen ? drawerWidth : 0,
            transition: "width 0.3s",
            overflowX: "hidden",
            boxSizing: "border-box",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

const SidebarItem = ({ text, icon, active, onClick, nested = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleItemClick = () => {
    onClick();
  };

  return (
    <ListItem disablePadding sx={{ pl: nested ? 4 : 2 }}>
      <ListItemButton
        onClick={handleItemClick}
        selected={active}
        sx={{
          mx: nested ? 0 : 1,
          borderRadius: "50px",
          backgroundColor: active ? "#FED304" : "transparent",
          "&.Mui-selected": {
            backgroundColor: "#FED304",
            fontWeight: "bold",
            borderRadius: "50px",
            color: "#0c5c88",
          },
        }}
      >
        <ListItemIcon sx={{ color: "#333", minWidth: 36 }}>{icon}</ListItemIcon>
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );
};

export default AdminDashboardSidebar;
